/**
 * AURA Color Adjustments Module
 * Contrast and saturation toggles use icon buttons; custom colors use pickers.
 */

import { ColorSettings } from '../../core/types';
import { stateManager } from '../../core/state';
import { eventBus } from '../../core/events';
import { Logger } from '../../core/logger';
import { createColorPicker } from '../../ui/controls';
import { createIconButton } from '../../ui/controls/icon-button';
import { getIcon } from '../../ui/styles/icons';
import { applyColorStyles } from './effects';

const logger = new Logger('ColorModule');

const CONTRAST_MODES = ['darkContrast', 'lightContrast', 'highContrast'] as const;
type ContrastMode = (typeof CONTRAST_MODES)[number];

interface ButtonRef {
  key: keyof ColorSettings;
  setChecked: (v: boolean) => void;
}

interface ColorPickerRef {
  key: keyof ColorSettings;
  setColor: (c: string | null) => void;
}

/**
 * Initialize the color adjustments module
 */
export function initColorModule(container: HTMLElement): void {
  logger.info('Initializing color module');

  const colorContainer = container.querySelector('#aura-color-adjustments');
  if (!colorContainer) {
    logger.warn('Color adjustments container not found');
    return;
  }

  const settings = stateManager.getSettings().color;
  const controls: HTMLElement[] = [];
  const buttonRefs: ButtonRef[] = [];
  const colorPickerRefs: ColorPickerRef[] = [];

  // --- Contrast section (icon button grid) ---

  const contrastLabel = document.createElement('h4');
  contrastLabel.className = 'aura-panel__subsection-title';
  contrastLabel.textContent = 'Contrast';
  controls.push(contrastLabel);

  const contrastGrid = document.createElement('div');
  contrastGrid.className = 'aura-icon-btn-grid';

  for (const def of buildContrastButtons(settings)) {
    const btn = createIconButton({
      id: def.id,
      label: def.label,
      icon: getIcon(def.icon as any),
      checked: def.checked,
      onChange: def.onChange,
    });
    contrastGrid.appendChild(btn.element);
    buttonRefs.push({ key: def.settingKey, setChecked: btn.setChecked });
  }

  controls.push(contrastGrid);

  // --- Saturation section (icon button grid) ---

  const satLabel = document.createElement('h4');
  satLabel.className = 'aura-panel__subsection-title';
  satLabel.textContent = 'Saturation';
  controls.push(satLabel);

  const satGrid = document.createElement('div');
  satGrid.className = 'aura-icon-btn-grid';

  for (const def of buildSaturationButtons(settings)) {
    const btn = createIconButton({
      id: def.id,
      label: def.label,
      icon: getIcon(def.icon as any),
      checked: def.checked,
      onChange: def.onChange,
    });
    satGrid.appendChild(btn.element);
    buttonRefs.push({ key: def.settingKey, setChecked: btn.setChecked });
  }

  controls.push(satGrid);

  // --- Custom colors (color pickers stay as-is) ---

  const colorLabel = document.createElement('h4');
  colorLabel.className = 'aura-panel__subsection-title';
  colorLabel.textContent = 'Custom Colors';
  controls.push(colorLabel);

  const textPicker = createColorPicker({
    id: 'aura-text-color',
    label: 'Adjust Text Colors',
    selectedColor: settings.textColor,
    onChange: (color) => updateSetting('textColor', color),
  });
  controls.push(textPicker.element);
  colorPickerRefs.push({ key: 'textColor', setColor: textPicker.setColor });

  const titlePicker = createColorPicker({
    id: 'aura-title-color',
    label: 'Adjust Title Colors',
    selectedColor: settings.titleColor,
    onChange: (color) => updateSetting('titleColor', color),
  });
  controls.push(titlePicker.element);
  colorPickerRefs.push({ key: 'titleColor', setColor: titlePicker.setColor });

  const bgPicker = createColorPicker({
    id: 'aura-background-color',
    label: 'Adjust Background Colors',
    selectedColor: settings.backgroundColor,
    onChange: (color) => updateSetting('backgroundColor', color),
  });
  controls.push(bgPicker.element);
  colorPickerRefs.push({ key: 'backgroundColor', setColor: bgPicker.setColor });

  // Render
  colorContainer.innerHTML = '';
  controls.forEach((c) => colorContainer.appendChild(c));

  // Keep all controls synced with external changes (e.g., toolbar reset)
  eventBus.on('settings:change', () => {
    const current = stateManager.getSettings().color;
    for (const ref of buttonRefs) {
      ref.setChecked(!!current[ref.key]);
    }
    for (const ref of colorPickerRefs) {
      ref.setColor(current[ref.key] as string | null);
    }
    applyColorStyles(current);
  });

  applyColorStyles(settings);
  logger.info('Color module initialized');
}

function handleContrastChange(mode: ContrastMode, enabled: boolean): void {
  if (enabled) {
    stateManager.updateSettings({
      color: {
        darkContrast: false,
        lightContrast: false,
        highContrast: false,
        [mode]: true,
      } as Partial<ColorSettings>,
    });
  } else {
    updateSetting(mode, false);
  }
}

function updateSetting<K extends keyof ColorSettings>(
  key: K,
  value: ColorSettings[K]
): void {
  stateManager.updateSettings({
    color: { [key]: value } as Partial<ColorSettings>,
  });
}

interface BtnDef {
  id: string;
  label: string;
  icon: string;
  checked: boolean;
  settingKey: keyof ColorSettings;
  onChange: (checked: boolean) => void;
}

function buildContrastButtons(s: ColorSettings): BtnDef[] {
  return [
    {
      id: 'aura-dark-contrast', label: 'Dark Contrast', icon: 'darkContrast',
      checked: s.darkContrast, settingKey: 'darkContrast',
      onChange: (checked) => handleContrastChange('darkContrast', checked),
    },
    {
      id: 'aura-light-contrast', label: 'Light Contrast', icon: 'lightContrast',
      checked: s.lightContrast, settingKey: 'lightContrast',
      onChange: (checked) => handleContrastChange('lightContrast', checked),
    },
    {
      id: 'aura-high-contrast', label: 'High Contrast', icon: 'highContrast',
      checked: s.highContrast, settingKey: 'highContrast',
      onChange: (checked) => handleContrastChange('highContrast', checked),
    },
  ];
}

function buildSaturationButtons(s: ColorSettings): BtnDef[] {
  return [
    {
      id: 'aura-high-saturation', label: 'High Saturation', icon: 'highSaturation',
      checked: s.highSaturation, settingKey: 'highSaturation',
      onChange: (checked) => updateSetting('highSaturation', checked),
    },
    {
      id: 'aura-low-saturation', label: 'Low Saturation', icon: 'lowSaturation',
      checked: s.lowSaturation, settingKey: 'lowSaturation',
      onChange: (checked) => updateSetting('lowSaturation', checked),
    },
    {
      id: 'aura-monochrome', label: 'Monochrome', icon: 'monochrome',
      checked: s.monochrome, settingKey: 'monochrome',
      onChange: (checked) => updateSetting('monochrome', checked),
    },
  ];
}
